import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL } from "@ffmpeg/util";
import { compressImageBlob } from "./compressImage";

const MAX_SIZE = 15 * 1024 * 1024;
const CDN_SOURCES = [
  "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd",
  "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd",
];

let ffmpeg = null;

const loadFFmpeg = async () => {
  let ffmpegInstance = new FFmpeg();

  for (let i = 0; i < CDN_SOURCES.length; i++) {
    try {
      await ffmpegInstance.load({
        coreURL: await toBlobURL(`${CDN_SOURCES[i]}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${CDN_SOURCES[i]}/ffmpeg-core.wasm`, "application/wasm"),
      });

      ffmpeg = ffmpegInstance;
      return;
    } catch (error) {
      console.error(`Failed to load FFmpeg from ${CDN_SOURCES[i]}`);

      if (i === CDN_SOURCES.length - 1) {
        throw new Error(
          `Failed to load FFmpeg from all CDN, the video will upload without compression`,
        );
      }
    }
  }
};

const cleanupFFmpeg = async () => {
  if (!ffmpeg) return;

  try {
    await ffmpeg.deleteFile("input.mp4");
    await ffmpeg.deleteFile("output.mp4");
    await ffmpeg.terminate();
  } catch (error) {
    console.error("An error occurred while cleaning up FFmpeg:", error);
  } finally {
    ffmpeg = null;
  }
};

const reduceVideoSize = async (videoBlob) => {
  try {
    await loadFFmpeg();
    const arrayBuffer = await videoBlob.arrayBuffer();

    await ffmpeg.writeFile("input.mp4", new Uint8Array(arrayBuffer));
    await ffmpeg.exec([
      "-i",
      "input.mp4",
      "-crf",
      "28",
      "-preset",
      "medium",
      "-vf",
      "scale='min(1920,iw)':-2",
      "-t",
      "60",
      "-vcodec",
      "libx264",
      "-movflags",
      "+faststart",
      "output.mp4",
    ]);

    const data = await ffmpeg.readFile("output.mp4");
    return new Blob([data.buffer], { type: "video/mp4" });
  } catch (error) {
    console.error("Error during the video compression", error);
    return videoBlob;
  } finally {
    await cleanupFFmpeg();
  }
};

export async function uploadMedia(uri, path, isVideo, options = {}) {
  try {
    const response = await fetch(uri);
    if (!response.ok) {
      console.log("Network request failed - Status:", response.status);
      throw new Error("Failed to fetch the image");
    }
    const blob = await response.blob();
    if (blob.size > MAX_SIZE) {
      throw new Error(`The file size cannot be more than ${MAX_SIZE} bytes`);
    }
    const finalBlob = isVideo
      ? await reduceVideoSize(blob)
      : options.compressImage
        ? await compressImageBlob(blob, options.compressImage)
        : blob;

    const storage = getStorage();
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, finalBlob);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (taskSnapshot) => {
          console.log(`${(taskSnapshot.bytesTransferred / taskSnapshot.totalBytes) * 100}%`);
        },
        (err) => {
          reject(err);
        },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        },
      );
    });
  } catch (err) {
    console.log("uploadMedia error:", err);
    throw err;
  }
}
